// src/scenes/stockout/CreateStockOut.jsx
import { useEffect, useState, useRef } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import { Formik, FieldArray } from "formik";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";


const token = localStorage.getItem("token");
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: { Authorization: `Bearer ${token}` },
});

/* ---------- API ---------- */
const getUsers = () =>
  api
    .get("/api/user", { params: { _t: Date.now() } })
    .then((r) =>
      (r.data?.data || []).filter(
        (u) => String(u?.role?.name || "").toLowerCase() === "sales"
      )
    );

const getItems = () =>
  api
    .get("/api/item", { params: { _t: Date.now() } })
    .then((r) => r.data?.data || []);

const getStoreItems = (warehouseId) =>
  api
    .get("/api/store", {
      params: { warehouseId, _t: Date.now() },
    })
    .then((r) => r.data?.data || []);

const getWarehouses = () =>
  api
    .get("/api/warehouse", { params: { _t: Date.now() } })
    .then((r) => r.data?.data || []);
const postStockOut = (body) =>
  api
    .post("/api/stockout", body)
    .then((r) => ({ data: r.data }))
    .catch((e) => ({ error: e.response?.data?.message || e.message }));



const SCHEMA = yup.object({
  userId: yup.number().required("Select user"),
  warehouseId: yup.number().required("Select warehouse"),
  
  description: yup.string().nullable(),
 items: yup
  .array()
  .of(
    yup.object({
      itemId: yup.number().required("Select item"),
      quantity: yup.number().positive().min(1).required(),
      unitPrice: yup.number().positive().required(),
    }),
  )
    .min(1, "Add at least one stock-out item"),
});

export default function CreateStockOut() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
 
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const loadOptions = async () => {
    setLoading(true);
    try {
      const [u, w] = await Promise.all([
        getUsers(),
      
        getWarehouses(),
      ]);
      if (!mounted.current) return;
      setUsers(Array.isArray(u) ? u : []);

     
      setWarehouses(Array.isArray(w) ? w : []);
    } catch {
      setToast({ open: true, msg: "Failed to load options", sev: "error" });
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const loadStoreItems = async (warehouseId) => {
  if (!warehouseId) {
    setItems([]);
    return;
  }

  try {
    const data = await getStoreItems(warehouseId);

    const mapped = data.map((s) => ({
      id: s.itemId,
      name: s.item?.name || `#${s.itemId}`,
      quantity: s.quantity,
    }));

    setItems(mapped);

    console.log("STORE ITEMS:", mapped);
  } catch (err) {
    console.error(err);
    setToast({
      open: true,
      msg: "Failed to load warehouse items",
      sev: "error",
    });
  }
};

  useEffect(() => {
    mounted.current = true;
    loadOptions();
    const interval = setInterval(loadOptions, 30000);
    const onFocus = () => loadOptions();
    window.addEventListener("focus", onFocus);
    return () => {
      mounted.current = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

 const INITIALS = {
  userId: "",
  warehouseId: "",
  description: "",
  items: [
    {
      itemId: "",
      quantity: 1,
      unitPrice: 0,
    },
  ],
};

  const submit = async (values, { setSubmitting, resetForm }) => {
   const payload = {
  userId: Number(values.userId),
  warehouseId: Number(values.warehouseId),
  description: values.description || "",
  items: values.items.map((i) => ({
    itemId: Number(i.itemId),
    quantity: Number(i.quantity),
    unitPrice: Number(i.unitPrice),
  })),
};

    const { data, error } = await postStockOut(payload);
    setSubmitting(false);

    if (error)
      return setToast({ open: true, msg: `Error: ${error}`, sev: "error" });
    setToast({ open: true, msg: "Stock-out saved!", sev: "success" });
    resetForm();
  };

  return (
    <Box m="20px">
      <Header title="CREATE STOCK-OUT" subtitle="Record stock-out items" />

      <Formik
        initialValues={INITIALS}
        validationSchema={SCHEMA}
        onSubmit={submit}
      >
        {(bag) => (
          <form onSubmit={bag.handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 4 !important",
                },
              }}
            >
              
              <TextField
                select
                label="User"
                name="userId"
                value={bag.values.userId}
                onChange={bag.handleChange}
                onBlur={bag.handleBlur}
                variant="filled"
                error={bag.touched.userId && Boolean(bag.errors.userId)}
                helperText={bag.touched.userId && bag.errors.userId}
              >
                {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
  {u.fullName} ({u.userName})
</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Warehouse"
                name="warehouseId"
                value={bag.values.warehouseId}
                onChange={(e) => {
                  bag.handleChange(e);
                  loadStoreItems(e.target.value);
                }}
                onBlur={bag.handleBlur}
                variant="filled"
                fullWidth
                error={
                  bag.touched.warehouseId && Boolean(bag.errors.warehouseId)
                }
                helperText={bag.touched.warehouseId && bag.errors.warehouseId}
              >
                {warehouses.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Description"
                name="description"
                value={bag.values.description}
                onChange={bag.handleChange}
                onBlur={bag.handleBlur}
                variant="filled"
                fullWidth
                multiline
                rows={2}
              />
             

              {/* .
              {/* Dynamic Items */}
              <FieldArray name="items">
                {({ push, remove }) => (
                  <Box sx={{ gridColumn: "span 3" }}>
                    {bag.values.items.map((item, index) => (
                      <Box
                        key={index}
                        display="grid"
                        gap="15px"
                        mt={2}
                        mb={1}
                        p={2}
                        borderRadius={2}
                        border="1px solid #4444"
                        gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                        sx={{
                          "& > div": {
                            gridColumn: isNonMobile
                              ? undefined
                              : "span 4 !important",
                          },
                        }}
                      >
                        <TextField
  select
  label="Item"
  name={`items[${index}].itemId`}
  value={item.itemId}
  onChange={bag.handleChange}
  variant="filled"
>
  {items.map((i) => (
    <MenuItem key={i.id} value={i.id}>
      {i.name} ({i.quantity})
    </MenuItem>
  ))}
</TextField>

                        <TextField
  type="number"
  label="Quantity"
  name={`items[${index}].quantity`}
  value={item.quantity}
  onChange={bag.handleChange}
  variant="filled"
  inputProps={{ min: 1 }}
/>

                       <TextField
  type="number"
  label="Unit Price"
  name={`items[${index}].unitPrice`}
  value={item.unitPrice}
  onChange={bag.handleChange}
  variant="filled"
  inputProps={{ min: 0 }}
/>

                        <Button
                          type="button"
                          color="error"
                          onClick={() => remove(index)}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() =>
                        push({ itemId: "", quantity: 1, unitPrice: 0 })
                      }
                    >
                      Add Item
                    </Button>
                  </Box>
                )}
              </FieldArray>
            </Box>

            {/* Buttons */}
            <Box display="flex" justifyContent="end" mt="20px" gap={2}>
              <Button onClick={() => bag.resetForm()} variant="outlined">
                Reset
              </Button>
              <Box position="relative" display="inline-flex">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={bag.isSubmitting} // disable while loading
                >
                  Create stockout
                </Button>

                {bag.isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "primary.main",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px", // half of size
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Box>
            </Box>
          </form>
        )}
      </Formik>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={toast.sev}
          variant="filled"
          onClose={() => setToast((s) => ({ ...s, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
